import { Controller } from "react-hook-form";
import { type ReactNode } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Eye, ShieldAlert, Check } from "lucide-react";
import { Combobox } from "@/modern/components/ui/combobox";
import { cn } from "@/modern/utils";
import { MarketingTagType } from "@/bryntum-lib/models/MarketingTag";
import { usePromotePanel, PROMOTE_STEPS } from "../context/PromotePanelContext";
import { PromoteStepHelpTrigger } from "./PromoteStepHelpTrigger";

export const SummaryStep = () => {
  const { form, restrictionReasonOptions, allTags, temporalTags } = usePromotePanel();
  const { setValue } = form;
  const watchIsRestricted = form.watch("isRestricted");
  const watchTags = form.watch("tags");
  const watchTemporalTagId = form.watch("temporalTagId");
  const watchCaption = form.watch("caption");
  const watchAltText = form.watch("altText");
  const watchRestrictionReason = form.watch("restrictionReason");
  const { errors } = form.formState;

  const temporalLabel = temporalTags.find((t) => t.id === watchTemporalTagId)?.label ?? "—";
  const selectedSolutionTags = (watchTags || []).flatMap((id) => {
    const tag = allTags.find((t) => t.id === id);
    return tag && !tag.isFolderEligible && tag.type !== MarketingTagType.PROBLEM && tag.type !== MarketingTagType.META ? [tag] : [];
  });
  const selectedContentRoles = (watchTags || []).flatMap((id) => {
    const tag = allTags.find((t) => t.id === id);
    return tag && tag.isFolderEligible ? [tag] : [];
  });
  const restrictionReasonLabel =
    restrictionReasonOptions.find((opt) => opt.value === watchRestrictionReason)?.label ?? "—";

  const visibilityOptions = [
    {
      value: "public",
      label: "Public Eligible",
      description: "Visible to all project members",
      icon: Eye,
    },
    {
      value: "restricted",
      label: "Restricted",
      description: "Limited visibility with reason",
      icon: ShieldAlert,
    },
  ];

  return (
    <div className="space-y-5">
      {/* Visibility */}
      <div>
        <div className="flex items-center justify-between gap-2 mb-2">
          <span className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Visibility
          </span>
          <PromoteStepHelpTrigger
            stepLabel={PROMOTE_STEPS[3].label}
            helpText={PROMOTE_STEPS[3].helpText}
          />
        </div>
        <Controller
          name="isRestricted"
          control={form.control}
          render={({ field }) => (
            <div className="space-y-1.5">
              {visibilityOptions.map((option) => {
                const isSelected =
                  (option.value === "public" && !field.value) ||
                  (option.value === "restricted" && field.value);
                const Icon = option.icon;

                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      const restricted = option.value === "restricted";
                      field.onChange(restricted);
                      if (!restricted) {
                        setValue("restrictionReason", null);
                      }
                    }}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-3.5 rounded-lg border text-left transition-all duration-150",
                      isSelected
                        ? "border-primary bg-primary/10 shadow-[0_0_0_1px_hsl(var(--primary)/0.3)]"
                        : "border-border hover:border-muted-foreground/40 hover:bg-muted/30",
                    )}
                  >
                    <Icon
                      className={cn(
                        "w-4 h-4 shrink-0 transition-colors duration-150",
                        isSelected ? "text-primary" : "text-muted-foreground",
                      )}
                    />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-semibold block text-foreground">
                        {option.label}
                      </span>
                      <span className="text-sm text-muted-foreground">{option.description}</span>
                    </div>
                    {isSelected && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 600, damping: 25 }}
                      >
                        <Check className="w-4 h-4 text-primary" />
                      </motion.div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        />
      </div>

      {/* Restriction Reason */}
      <AnimatePresence>
        {watchIsRestricted && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="pb-1">
              <label className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-2 block">
                Restriction Reason
              </label>
              <Controller
                name="restrictionReason"
                control={form.control}
                render={({ field }) => (
                  <Combobox
                    options={restrictionReasonOptions}
                    value={field.value ?? undefined}
                    onValueChange={(val) => field.onChange(val ?? null)}
                    placeholder="Select reason..."
                    searchPlaceholder="Search reasons..."
                    emptyMessage="No reasons found."
                    size="sm"
                  />
                )}
              />
              {errors.restrictionReason && (
                <p className="text-sm text-destructive mt-1">{errors.restrictionReason.message}</p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Summary Card */}
      <div>
        <label className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-2 block">
          Summary
        </label>
        <div className="rounded-lg border border-border p-3.5 space-y-4">
          <SummarySection>
            <SummaryTextRow label="Phase" value={temporalLabel} />
          </SummarySection>

          <SummarySection>
            <SummaryBadgeRow
              label="Tags"
              tags={selectedSolutionTags.map((tag) => tag.label ?? "Untitled")}
              variant="solution"
              emptyValue="None selected"
            />
            <SummaryTextRow label="Caption" value={watchCaption || "—"} />
            <SummaryTextRow label="Alt text" value={watchAltText || "—"} />
          </SummarySection>

          <SummarySection>
            <SummaryBadgeRow
              label="Content Roles"
              tags={selectedContentRoles.map((tag) => tag.label ?? "Untitled")}
              variant="role"
              emptyValue="None selected"
            />
          </SummarySection>

          <SummarySection>
            <SummaryTextRow label="Visibility" value={watchIsRestricted ? "Restricted" : "Public Eligible"} />
            {watchIsRestricted ? (
              <SummaryTextRow label="Reason" value={restrictionReasonLabel} />
            ) : null}
          </SummarySection>
        </div>
      </div>
    </div>
  );
};

const SummarySection = ({ children }: { children: ReactNode }) => (
  <div className="space-y-2">
    <div className="space-y-2">{children}</div>
  </div>
);

const SummaryTextRow = ({ label, value }: { label: string; value: string }) => (
  <div className="grid grid-cols-[96px_1fr] gap-2">
    <span className="text-sm font-medium text-muted-foreground">{label}</span>
    <span className="text-sm text-foreground leading-relaxed whitespace-pre-wrap break-words">{value}</span>
  </div>
);

const SummaryBadgeRow = ({
  label,
  tags,
  variant,
  emptyValue,
}: {
  label: string;
  tags: string[];
  variant: "solution" | "role";
  emptyValue: string;
}) => (
  <div className="grid grid-cols-[96px_1fr] gap-2">
    <span className="text-sm font-medium text-muted-foreground">{label}</span>
    {tags.length > 0 ? (
      <div className="flex flex-wrap gap-1.5">
        {tags.map((tag) => (
          <span
            key={`${variant}-${tag}`}
            className={cn(
              "inline-flex items-center rounded-full border px-2.5 py-1 text-sm font-medium",
              variant === "solution"
                ? "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/25"
                : "bg-violet-500/15 text-violet-300 border-violet-500/30",
            )}
          >
            {tag}
          </span>
        ))}
      </div>
    ) : (
      <span className="text-sm text-foreground leading-relaxed">{emptyValue}</span>
    )}
  </div>
);
